import { Office, Client, LegalFile, Role } from '../types';
import { UsageStats } from '../types/subscription';

/**
 * Local Usage Calculation Service
 * 
 * This service calculates usage statistics locally from cached data
 * instead of relying on Firestore usageStats collection.
 * 
 * Benefits:
 * - Faster performance (no Firestore queries)
 * - Real-time updates from cache
 * - Simplified architecture
 * - Always up-to-date with current data
 */

export interface LocalUsageStats {
  userId: string;
  officesCount: number;
  clientsCount: number;
  filesCount: number;
  teamMembersCount: number;
  lastUpdated: string;
  officeUsage: Array<{
    officeId: string;
    officeName: string;
    clientsCount: number;
    filesCount: number;
    lastUpdated: string;
  }>;
}

export class LocalUsageService {
  /**
   * Calculate usage statistics from cached data
   */
  static calculateUsageStats(
    userId: string,
    offices: Office[],
    clients: Client[],
    legalFiles: LegalFile[],
    roles: Role[]
  ): LocalUsageStats {
    console.log('📊 LocalUsageService: Calculating usage stats for user:', userId);
    console.log('📊 - Offices:', offices.length);
    console.log('📊 - Clients:', clients.length);
    console.log('📊 - Legal Files:', legalFiles.length);
    console.log('📊 - Roles:', roles.length);

    const now = new Date().toISOString();
    
    // Filter out deleted/archived items
    const activeOffices = offices.filter(office => !office.isDeleted);
    const activeClients = clients.filter(client => !client.isDeleted);
    const activeLegalFiles = legalFiles.filter(file => !file.isDeleted);
    const activeRoles = roles.filter(role => !role.isArchived && !role.isDeleted);

    console.log('📊 - Active Offices:', activeOffices.length);
    console.log('📊 - Active Clients:', activeClients.length);
    console.log('📊 - Active Legal Files:', activeLegalFiles.length);

    // Calculate office usage details with proper relationships
    const officeUsage = activeOffices.map(office => {
      // Find all roles that belong to this office
      const officeRoles = roles.filter(role => 
        role.officeID === office.id && 
        !role.isArchived && 
        !role.isDeleted
      );
      
      // Get role IDs for this office
      const officeRoleIds = officeRoles.map(role => role.id);
      
      // Find clients that have any of these role IDs
      const officeClients = activeClients.filter(client => 
        client.rolesIDs && 
        client.rolesIDs.some(roleId => officeRoleIds.includes(roleId))
      );
      
      // Find legal files that belong to these clients
      const officeFiles = activeLegalFiles.filter(file => 
        officeClients.some(client => client.id === file.clientId || client.id === file.clientID)
      );

      return {
        officeId: office.id,
        officeName: office.name,
        clientsCount: officeClients.length,
        filesCount: officeFiles.length,
        lastUpdated: now
      };
    });

    // Calculate total counts
    const totalClients = activeClients.length;
    const totalFiles = activeLegalFiles.length;

    const usageStats: LocalUsageStats = {
      userId,
      officesCount: activeOffices.length,
      clientsCount: totalClients,
      filesCount: totalFiles,
      teamMembersCount: activeRoles.length,
      lastUpdated: now,
      officeUsage
    };

    console.log('📊 LocalUsageService: Calculated usage stats:', usageStats);
    return usageStats;
  }

  /**
   * Convert LocalUsageStats to UsageStats format for compatibility
   */
  static toUsageStats(localStats: LocalUsageStats): UsageStats {
    return {
      userId: localStats.userId,
      officesCount: localStats.officesCount,
      clientsCount: localStats.clientsCount,
      filesCount: localStats.filesCount,
      lastUpdated: localStats.lastUpdated,
      officeUsage: localStats.officeUsage.map(office => ({
        officeId: office.officeId,
        officeName: office.officeName,
        clientsCount: office.clientsCount,
        filesCount: office.filesCount,
        lastUpdated: office.lastUpdated
      }))
    };
  }

  /**
   * Get usage statistics for a specific office
   */
  static getOfficeUsage(
    officeId: string,
    clients: Client[],
    legalFiles: LegalFile[],
    roles: Role[]
  ): { clientsCount: number; filesCount: number } {
    // Find all roles that belong to this office
    const officeRoles = roles.filter(role => 
      role.officeID === officeId && 
      !role.isArchived && 
      !role.isDeleted
    );
    
    // Get role IDs for this office
    const officeRoleIds = officeRoles.map(role => role.id);
    
    // Find clients that have any of these role IDs
    const activeClients = clients.filter(client => 
      !client.isDeleted && 
      client.rolesIDs && 
      client.rolesIDs.some(roleId => officeRoleIds.includes(roleId))
    );

    // Find legal files that belong to these clients
    const activeFiles = legalFiles.filter(file =>
      !file.isDeleted &&
      activeClients.some(client => client.id === file.clientId || client.id === file.clientID)
    );

    return {
      clientsCount: activeClients.length,
      filesCount: activeFiles.length
    };
  }

  /**
   * Check if user can create more of a specific resource
   */
  static canCreateResource(
    currentCount: number,
    maxCount: number
  ): boolean {
    // -1 means unlimited
    if (maxCount === -1) return true;
    return currentCount < maxCount;
  }

  /**
   * Get usage percentage for a resource
   */
  static getUsagePercentage(
    currentCount: number,
    maxCount: number
  ): number {
    if (maxCount === -1) return 0; // Unlimited
    if (maxCount === 0) return 100; // No limit but current count > 0
    return Math.round((currentCount / maxCount) * 100);
  }

  /**
   * Check if usage is approaching limit (80% threshold)
   */
  static isUsageWarning(currentCount: number, maxCount: number): boolean {
    return this.getUsagePercentage(currentCount, maxCount) >= 80;
  }

  /**
   * Check if usage is critical (95% threshold)
   */
  static isUsageCritical(currentCount: number, maxCount: number): boolean {
    return this.getUsagePercentage(currentCount, maxCount) >= 95;
  }
}

export default LocalUsageService;
